import {
    memo,
    useState,
    useMemo,
    useCallback,
    lazy,
    Suspense,
} from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import clsx from 'clsx';

import Aside from './Aside';
import CourseInfoSection from './CourseInfoSection';
import DndTimetableProvider from './DndTimetableProvider';
import ErrorBoundary from './ErrorBoundary';
import Header from './Header';
import Timetable from './Timetable';

import { API_ENDPOINT, DEFAULT_TERM } from '../constants';
import DataContext from '../context';
import { getStoredTerm } from '../utils/plannerStorage';

import styles from './App.module.css';

// Lazy load dialogs for code splitting (Phase 1.1)
const FaqDialog = lazy(() => import('./FaqDialog'));
const DownloadDialog = lazy(() => import('./DownloadDialog'));


const App = memo(() => {
    const [selectedTerm, setSelectedTerm] = useState(() => {
        const storedTerm = getStoredTerm();
        return /^\d{4}$/.test(storedTerm || '') ? storedTerm : DEFAULT_TERM;
    });
    const selectTerm = useCallback((term) => {
        setSelectedTerm(term);
    }, []);

    const { data, isLoading, isError } = useQuery({
        queryKey: ["data", selectedTerm],
        queryFn: () => {
            return axios.get(API_ENDPOINT, { params: { term: selectedTerm } }).then((res) => res.data);
        },
        refetchInterval: 0,
        retry: 1,
        // Don't throw errors, handle them gracefully
        throwOnError: false,
        enabled: Boolean(selectedTerm),
        // Performance optimizations: prevent unnecessary refetches
        staleTime: 5 * 60 * 1000, // 5 minutes - data rarely changes mid-session
        cacheTime: 30 * 60 * 1000, // 30 minutes - keep in cache for term switching
        refetchOnWindowFocus: false, // Don't refetch when user returns to tab
    });

    // Context value includes data and term setter
    const contextValue = useMemo(() => ({
        data,
        selectedTerm,
        setSelectedTerm: selectTerm,
    }), [data, selectTerm, selectedTerm]);

    const loadingDivStyle = {
        display: 'block',
        width: 200,
        margin: '0 auto',
        paddingTop: 50,
        fontSize: 50,
    };

    // Main content section - wraps both timetable and control panel in SAME provider
    const mainContent = selectedTerm && !isError && data && !isLoading ? (
        <ErrorBoundary>
            <DndTimetableProvider key={selectedTerm}>
                <div id="timetable_wrapper" className={clsx(styles.timetableWrapper, styles.noSelect)}>
                    <Timetable />
                </div>
                <div className={clsx(styles.controlPanel, 'content')}>
                    <div className={styles.courseInfoSectionContainer}>
                        <CourseInfoSection />
                    </div>
                    <div className={styles.asideContainer}>
                        <Aside />
                    </div>
                </div>
            </DndTimetableProvider>
        </ErrorBoundary>
    ) : null;

    return (
        <DataContext.Provider value={contextValue}>
            <div className={styles.app}>
                <Header />
                <div className={styles.container}>
                    {isError && selectedTerm && (
                        <div style={loadingDivStyle}>
                            <div style={{ fontSize: 20, color: '#d32f2f' }}>
                                Failed to load course data
                            </div>
                            <div style={{ fontSize: 14, marginTop: 10, color: '#666' }}>
                                Term: {selectedTerm}
                            </div>
                            <div style={{ fontSize: 14, marginTop: 5, color: '#999' }}>
                                Check if courseInfo_{selectedTerm}.json exists in R2 bucket
                            </div>
                        </div>
                    )}
                    {isLoading && (
                        <div style={loadingDivStyle}>
                            <div style={{ fontSize: 20, color: '#1976d2' }}>Loading course data...</div>
                        </div>
                    )}
                    {mainContent}
                    {/* Code-split dialogs (Phase 1.1) */}
                    <Suspense fallback={null}>
                        <FaqDialog />
                        <DownloadDialog />
                    </Suspense>
                </div>
            </div>
        </DataContext.Provider>
    );
});

export default App;
